import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// 🧱 Layouts
import { AppLayout } from "../components/layouts/app"; // pour les pages publiques



// 💫 Loader
import Loading from "../components/loading";
import AdminLayout from "../components/layouts/admindashboard";
import { AdminProvider } from "../context/AdminContext";
import RouteGuard from "../guards/authguard";
import { RoleGuard } from "../guards/RoleGuard";
import { UserProvider } from "../context/UserContext";
import UserLayout from "../components/layouts/userdashboard";
import AuthLayout from "../components/layouts/auth/AuthLayout";
import AdminCommerceLayout from "../context/layouts/AdminCommerceLayout";

/* =============================
   🔐 AUTH
============================= */
const LoginPage = lazy(() => import("../pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("../pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("../pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/auth/ResetPasswordPage"));
const VerifyCodePage = lazy(() => import("../pages/auth/VerifyCodePage"));

const UnauthorizedPage = lazy(() => import("../pages/UnauthorizedPage"));

/* =============================
   🌍 PAGES PUBLIQUES
============================= */

const HomePage = lazy(() => import("../pages/app/home/home"));
const AboutPage = lazy(() => import("../pages/app/apropos"));
const ProductsPage = lazy(() => import("../pages/app/boutique/products"));
const ContactPage = lazy(() => import("../pages/app/contact"));
const BlogPage = lazy(() => import("../pages/app/blog/blog"));
const BlogDetailPage = lazy(() => import("../pages/app/blog/blogDetail"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const CheckoutPage = lazy(() => import("../pages/app/checkout"));




/* =============================
   🧭 PAGES DASHBOARD User
============================= */
const UserDashboardHomePage = lazy(() => import("../pages/userdashboard/userhome"));
//Mes achats
const UserCommandsPage = lazy(() => import("../pages/userdashboard/commandes/index"));
const UserPaiementPage = lazy(() => import("../pages/userdashboard/paiements/index"));
const UserProductsPage = lazy(() => import("../pages/userdashboard/products/index"));
// Mon compte
const UserProfilePage = lazy(() => import("../pages/userdashboard/profile/index"));
const UserSecurityPage = lazy(() => import("../pages/userdashboard/security/index"));

/* =============================
   🧭 PAGES DASHBOARD ADMIN
============================= */
const DashboardHomePage = lazy(() => import("../pages/admindashboard/adminhome"));
// Administration
const ProductCategoriesPage = lazy(() => import("../pages/admindashboard/product-categories/index"));
const ProductCategoriesDetailPage = lazy(() => import("../pages/admindashboard/product-categories/show/inndex"));
const DashboardProductsPage = lazy(() => import("../pages/admindashboard/products/index"));
const DashboardProductsDetailPage = lazy(() => import("../pages/admindashboard/products/show/inndex"));
const ArticlesCategoriesPage = lazy(() => import("../pages/admindashboard/article-categories/index"));
const ArticlesCategoriesDetailPage = lazy(() => import("../pages/admindashboard/article-categories/show/inndex"));
const ArticlesPage = lazy(() => import("../pages/admindashboard/articles/index"));
const ArticlesDetailPage = lazy(() => import("../pages/admindashboard/articles/show/inndex"));
const DashboardServicesPage = lazy(() => import("../pages/admindashboard/services/index"));
const ServiceDetailPage = lazy(() => import("../pages/admindashboard/services/show/inndex"));

const DashboardAdminUsersPage = lazy(() => import("../pages/admindashboard/users/index"));
const AdminNotificationsPage = lazy(() => import("../pages/admindashboard/admin-notifications/index"));
const DashboardSettingsPage = lazy(() => import("../pages/admindashboard/settings/index"));
const PreferencesPage = lazy(() => import("../pages/admindashboard/preferences/index"));


// Commerce
const DashboardOrdersPage = lazy(() => import("../pages/admindashboard/commerces/orders/index"));
const DashboardCustomersPage = lazy(() => import("../pages/admindashboard/commerces/customers/index"));
const DashboardProductStatsPage = lazy(() => import("../pages/admindashboard/commerces/product-stats/index"));

// finances
const DashboardTransactionsPage = lazy(() => import("../pages/admindashboard/finances/transactions/index"));
const DashboardReportsPage = lazy(() => import("../pages/admindashboard/finances/reports/index"));


// Compte
const ProfilePage = lazy(() => import("../pages/admindashboard/profile/index"));
const SecurityPage = lazy(() => import("../pages/admindashboard/security/index"));


/* =============================
   🚀 ROUTEUR PRINCIPAL
============================= */
const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>

        {/* ===================== Authentification ===================== */}

        <Route element={<RouteGuard isAuth />}>
          <Route element={<AuthLayout />}>
            <Route path="/auth/login" element={<Suspense fallback={<Loading />}><LoginPage /></Suspense>} />
            <Route path="/auth/register" element={<Suspense fallback={<Loading />}><RegisterPage /></Suspense>} />
            <Route path="/auth/forgot-password" element={<Suspense fallback={<Loading />}><ForgotPasswordPage /></Suspense>} />
            <Route path="/auth/reset-password" element={<Suspense fallback={<Loading />}><ResetPasswordPage /></Suspense>} />
            <Route path="/auth/verify-code" element={<Suspense fallback={<Loading />}><VerifyCodePage /></Suspense>} />
          </Route>
        </Route>


        {/* ===================== PUBLIC ===================== */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<Suspense fallback={<Loading />}><HomePage /></Suspense>} />
          <Route path="/about" element={<Suspense fallback={<Loading />}><AboutPage /></Suspense>} />
          <Route path="/products" element={<Suspense fallback={<Loading />}><ProductsPage /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<Loading />}><ContactPage /></Suspense>} />
          <Route path="/blog" element={<Suspense fallback={<Loading />}><BlogPage /></Suspense>} />
          <Route path="/blog/:id" element={<Suspense fallback={<Loading />}><BlogDetailPage /></Suspense>} />
          <Route path="/checkout" element={<Suspense fallback={<Loading />}><CheckoutPage /></Suspense>} />
        </Route>




        {/* ===================== DASHBOARD USER ===================== */}
        <Route element={<RouteGuard isPrivate />}>
          <Route element={<RoleGuard roles={["user"]} />}>
            <Route element={<UserProvider><UserLayout /></UserProvider>}>

              {/* Principale */}
              <Route path="/user/dashboard/home" element={<Suspense fallback={<Loading />}><UserDashboardHomePage /></Suspense>} />
              {/* Mes achats */}
              <Route path="/user/dashboard/commands" element={<Suspense fallback={<Loading />}><UserCommandsPage /></Suspense>} />
              <Route path="/user/dashboard/paiements" element={<Suspense fallback={<Loading />}><UserPaiementPage /></Suspense>} />
              <Route path="/user/dashboard/products" element={<Suspense fallback={<Loading />}><UserProductsPage /></Suspense>} />
              {/* Mon compte */}
              <Route path="/user/dashboard/profile" element={<Suspense fallback={<Loading />}><UserProfilePage /></Suspense>} />
              <Route path="/user/dashboard/security" element={<Suspense fallback={<Loading />}><UserSecurityPage /></Suspense>} />

            </Route>
          </Route>
        </Route>


        {/* ===================== DASHBOARD ADMIN ===================== */}
        <Route element={<RouteGuard isPrivate />}>
          <Route element={<RoleGuard roles={["admin", "platform_admin"]} />}>
            <Route
              element={
                <AdminProvider>
                  <AdminLayout />
                </AdminProvider>
              }
            >

              {/* Contenu */}
              <Route path="/admin/dashboard/product-categories" element={<Suspense fallback={<Loading />}><ProductCategoriesPage /></Suspense>} />
              <Route path="/admin/dashboard/product-categories/:id" element={<Suspense fallback={<Loading />}><ProductCategoriesDetailPage /></Suspense>} />
              <Route path="/admin/dashboard/products" element={<Suspense fallback={<Loading />}><DashboardProductsPage /></Suspense>} />
              <Route path="/admin/dashboard/products/:id" element={<Suspense fallback={<Loading />}><DashboardProductsDetailPage /></Suspense>} />
              <Route path="/admin/dashboard/services" element={<Suspense fallback={<Loading />}><DashboardServicesPage /></Suspense>} />
              <Route path="/admin/dashboard/services/:id" element={<Suspense fallback={<Loading />}><ServiceDetailPage /></Suspense>} />

              {/* article-categories */}
              <Route path="/admin/dashboard/article-categories" element={<Suspense fallback={<Loading />}><ArticlesCategoriesPage /></Suspense>} />
              <Route path="/admin/dashboard/article-categories/:id" element={<Suspense fallback={<Loading />}><ArticlesCategoriesDetailPage /></Suspense>} />
              {/* articles */}
              <Route path="/admin/dashboard/articles" element={<Suspense fallback={<Loading />}><ArticlesPage /></Suspense>} />
              <Route path="/admin/dashboard/articles/:id" element={<Suspense fallback={<Loading />}><ArticlesDetailPage /></Suspense>} />

              {/* Commerce */}
              <Route element={<AdminCommerceLayout />}>
                {/* Principale */}
                <Route path="/admin/dashboard/home" element={<Suspense fallback={<Loading />}><DashboardHomePage /></Suspense>} />
                <Route path="/admin/dashboard/commerces/orders" element={<Suspense fallback={<Loading />}><DashboardOrdersPage /></Suspense>} />
                <Route path="/admin/dashboard/commerces/customers" element={<Suspense fallback={<Loading />}><DashboardCustomersPage /></Suspense>} />
                <Route path="/admin/dashboard/finances/transactions" element={<Suspense fallback={<Loading />}><DashboardTransactionsPage /></Suspense>} />
                <Route path="/admin/dashboard/finances/reports" element={<Suspense fallback={<Loading />}><DashboardReportsPage /></Suspense>} />
                <Route path="/admin/dashboard/commerces/product-stats" element={<Suspense fallback={<Loading />}><DashboardProductStatsPage /></Suspense>} />
              </Route>


              {/* Administration */}
              <Route path="/admin/dashboard/users" element={<Suspense fallback={<Loading />}><DashboardAdminUsersPage /></Suspense>} />
              <Route path="/admin/dashboard/admin-notifications" element={<Suspense fallback={<Loading />}><AdminNotificationsPage /></Suspense>} />
              <Route path="/admin/dashboard/preferences" element={<Suspense fallback={<Loading />}><PreferencesPage /></Suspense>} />
              <Route path="/admin/dashboard/settings" element={<Suspense fallback={<Loading />}><DashboardSettingsPage /></Suspense>} />

              {/* Compte */}
              <Route path="/admin/dashboard/profile" element={<Suspense fallback={<Loading />}><ProfilePage /></Suspense>} />
              <Route path="/admin/dashboard/security" element={<Suspense fallback={<Loading />}><SecurityPage /></Suspense>} />


            </Route>
          </Route>
        </Route>

        {/* ===================== 404 ===================== */}
        <Route path="*" element={<Suspense fallback={<Loading />}><NotFoundPage /></Suspense>} />
        <Route path="/unauthorized" element={<Suspense fallback={<Loading />}><UnauthorizedPage /></Suspense>} />

      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
